/**
 * Enemy roster.
 *
 * Design rule: a family is only worth having if it *invalidates* a tower that
 * worked last round. Eight families, eight different wrong answers:
 *
 *  grunt     — the control group. Everything kills it. Nothing kills it fast.
 *  runner    — outruns slow projectiles. Punishes lobbed and travel-time shots.
 *  armoured  — flat reduction per hit, so DPS-by-volume collapses. Punishes
 *              rapid fire with low per-shot damage.
 *  swarmling — forty bodies at once. Punishes single-target towers.
 *  weaver    — sine-weaves the full corridor width. Punishes narrow cones and
 *              anything that aims where the target *is* rather than will be.
 *  splitter  — four generations deep. Punishes overkill and thin coverage
 *              behind the kill zone.
 *  shielded  — a regenerating overshield. Punishes burst that cannot finish
 *              the shield in one window; rewards sustained pressure.
 *  boss      — combines two of the above and adds a leak cost you cannot eat.
 *
 * Numbers are round-1 base values. `Waves.ts` scales hp by the round curve;
 * speed, armour and shield regen are deliberately *not* scaled, so late rounds
 * get fatter rather than uniformly harder to react to.
 */

import { ROLE } from '@/core/palette';
import type { EnemyArchetype, EnemyFamily } from '@/core/types';

/**
 * Per-archetype motion flavour. Kept out of `EnemyArchetype` because it is
 * presentation-adjacent and owned entirely by the enemy sim.
 */
export interface MotionProfile {
  /** Lateral sine amplitude as a fraction of corridor half-width. */
  weaveAmp: number;
  /** Weave cycles per second. */
  weaveHz: number;
  /** Random per-instance lane offset, fraction of half-width. */
  laneSpread: number;
  /** Per-instance speed variation, +/- this fraction. */
  speedJitter: number;
  /** Banking stiffness. Higher snaps to heading, lower drifts. */
  bankStiffness: number;
  /** Banking damping ratio driver. Below 2*sqrt(stiffness) it overshoots. */
  bankDamping: number;
}

export const DEFAULT_MOTION: MotionProfile = {
  weaveAmp: 0,
  weaveHz: 0,
  laneSpread: 0.42,
  speedJitter: 0.06,
  bankStiffness: 64,
  bankDamping: 7.2,
};

const motion = (p: Partial<MotionProfile>): MotionProfile => ({ ...DEFAULT_MOTION, ...p });

/** Overrides keyed by archetype id. Anything absent uses `DEFAULT_MOTION`. */
export const MOTION: Record<string, MotionProfile> = {};

// ───────────────────────────────────────────────────────────── authoring ──

const ENEMY_LIST: EnemyArchetype[] = [];

function define(a: EnemyArchetype, m?: Partial<MotionProfile>): EnemyArchetype {
  ENEMY_LIST.push(a);
  if (m) MOTION[a.id] = motion(m);
  return a;
}

// ───────────────────────────────────────────────────────────────── grunt ──
// The reference point every other number is expressed against. 40hp at 34px/s
// crosses the 1600px serpentine in 47 seconds.

define({
  id: 'grunt', family: 'grunt', glyph: 'grunt', colour: ROLE.grunt,
  hp: 40, speed: 34, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 1.0, radius: 5, flocks: false,
});

/**
 * HEAVY, act I. The round-9 debut, and the first time plate matters.
 *
 * The plate is 1, not 2, and the reason is that two was not a lesson, it was a
 * wall. Flat armour is subtracted *per hit*, so what it costs depends entirely
 * on how big the hit is — and act I's hits are tiny. At armour 2 a bare Turret
 * landed **1.60 of its 4.0 damage, 40%**, and a bare Flak Battery landed
 * **0.43 of 3.0, fourteen percent**, because Flak carries `armourPierce: 0.9`
 * on top. A player who spent act I's wallet on width rather than depth met this
 * on round 9 and had no answer at all: three playtests died on rounds 10 and 11
 * with four, eight and nine towers built, and the death round did not move with
 * the tower count because more bare barrels is not more damage against plate.
 *
 * At 1 the same Turret lands 2.6 of 4.0 and the same Flak 1.43 of 3.0. Plate
 * still hurts, upgrading still fixes it — a Turret on Sabot Rounds reads 93% —
 * and the answer is now reachable inside act I's ten slots rather than only
 * after it.
 */
define({
  id: 'grunt.heavy', family: 'grunt', glyph: 'gruntHeavy', colour: ROLE.grunt,
  hp: 130, speed: 28, armour: 1, shield: 0, shieldRegenPerSec: 0,
  leak: 2, bounty: 1.05, radius: 6, flocks: false,
  resist: { kinetic: 0.9 },
});

/**
 * MONOLITH, act III. Three hundred and twenty hit points behind four of plate,
 * walking at three quarters of a grunt.
 *
 * It carries no mechanic at all, which is the mechanic. Act III's rounds ask for
 * hundreds of thousands of effective hit points and the roster it had could only
 * answer in *bodies* — ninety heavies plus ninety armoured plus whatever else
 * fitted, every group pinned at `Waves.CAP_QUEUE`, and a board whose splash and
 * pierce multipliers were being handed three hundred targets a round. The counts
 * were the difficulty and the counts had run out: rounds 35, 36 and 48 landed at
 * 69–78% of what the curve asked for (docs/BALANCE.md §7.5).
 *
 * A monolith is two and a half heavies of mass in one body, so a group of forty
 * carries what a hundred used to and every splash radius on the map catches less
 * of it. That it is heavier than `grunt.elite`, which arrives fourteen rounds
 * later, is deliberate: a body that is a heavy in act III is filler in act IV,
 * and the roster is a curriculum rather than a ladder.
 */
define({
  id: 'grunt.monolith', family: 'grunt', glyph: 'gruntMonolith', colour: ROLE.grunt,
  hp: 320, speed: 26, armour: 4, shield: 0, shieldRegenPerSec: 0,
  leak: 4, bounty: 1.25, radius: 10, flocks: false,
  resist: { kinetic: 0.75, explosive: 0.85 },
  immune: ['knockback', 'stun'],
}, { laneSpread: 0.18, speedJitter: 0.03, bankStiffness: 28, bankDamping: 9.4 });

define({
  id: 'grunt.elite', family: 'grunt', glyph: 'gruntElite', colour: ROLE.grunt,
  hp: 340, speed: 32, armour: 5, shield: 0, shieldRegenPerSec: 0,
  leak: 3, bounty: 1.1, radius: 7, flocks: false,
  resist: { kinetic: 0.85, corrosive: 0.9 },
  immune: ['knockback'],
});

// ──────────────────────────────────────────────────────────────── runner ──
// Fast and fragile. Anything with travel time misses; anything with a wind-up
// never fires. The counter is hitscan, high fire rate, or a slow field —
// except `runner.blink`, which is immune to slow specifically to stop one
// cryo tower solving the whole family.

define({
  id: 'runner', family: 'runner', glyph: 'runner', colour: ROLE.runner,
  hp: 26, speed: 92, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 1.15, radius: 4, flocks: false,
  resist: { explosive: 0.75 },
  immune: ['knockback'],
}, { laneSpread: 0.55, speedJitter: 0.1, bankStiffness: 96, bankDamping: 6.4 });

define({
  id: 'runner.blink', family: 'runner', glyph: 'runnerBlink', colour: ROLE.runner,
  hp: 44, speed: 128, armour: 1, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 1.3, radius: 4, flocks: false,
  resist: { explosive: 0.6, kinetic: 0.85 },
  immune: ['slow', 'knockback'],
}, { laneSpread: 0.6, speedJitter: 0.12, bankStiffness: 120, bankDamping: 6.0 });

define({
  id: 'runner.courier', family: 'runner', glyph: 'runnerCourier', colour: ROLE.runner,
  hp: 240, speed: 108, armour: 3, shield: 0, shieldRegenPerSec: 0,
  leak: 3, bounty: 1.4, radius: 6, flocks: false,
  resist: { explosive: 0.7 },
  immune: ['slow', 'stun', 'knockback'],
}, { laneSpread: 0.3, speedJitter: 0.04, bankStiffness: 88, bankDamping: 6.8 });

/**
 * VECTOR, act VI. 140px/s — four times a grunt — with 620 hit points behind it.
 * `Waves.demandOf` divides its share of the budget by its pace, so a round
 * drawing vectors gets *few* of them; the threat is that those few cross the
 * maelstrom in seventeen seconds and no lobbed shell ever arrives.
 */
define({
  id: 'runner.vector', family: 'runner', glyph: 'runnerVector', colour: ROLE.runner,
  hp: 620, speed: 140, armour: 5, shield: 0, shieldRegenPerSec: 0,
  leak: 4, bounty: 1.5, radius: 6, flocks: false,
  resist: { explosive: 0.55, kinetic: 0.8 },
  immune: ['slow', 'stun', 'knockback'],
}, { laneSpread: 0.34, speedJitter: 0.05, bankStiffness: 132, bankDamping: 5.6 });

// ────────────────────────────────────────────────────────────── armoured ──
// Armour is flat *per hit*. A 4-damage minigun at 12 shots/sec does nothing to
// an armour-8 target; a 60-damage cannon at 0.8 shots/sec walks through it.
// Kinetic resistance stacks on top so the obvious "just add armour pierce"
// answer still wants an energy or corrosive second tower.

define({
  id: 'armoured', family: 'armoured', glyph: 'armoured', colour: ROLE.armoured,
  hp: 78, speed: 22, armour: 4, shield: 0, shieldRegenPerSec: 0,
  leak: 2, bounty: 1.2, radius: 6, flocks: false,
  resist: { kinetic: 0.70, energy: 0.9 },
  immune: ['knockback', 'stun'],
}, { laneSpread: 0.25, speedJitter: 0.03, bankStiffness: 34, bankDamping: 9.0 });

define({
  id: 'armoured.bulwark', family: 'armoured', glyph: 'armouredBulwark', colour: ROLE.armoured,
  hp: 280, speed: 18, armour: 8, shield: 0, shieldRegenPerSec: 0,
  leak: 3, bounty: 1.3, radius: 7, flocks: false,
  resist: { kinetic: 0.45, energy: 0.8, explosive: 0.85 },
  immune: ['knockback', 'stun', 'slow'],
}, { laneSpread: 0.2, speedJitter: 0.02, bankStiffness: 30, bankDamping: 9.6 });

define({
  id: 'armoured.siege', family: 'armoured', glyph: 'armouredSiege', colour: ROLE.armoured,
  hp: 760, speed: 15, armour: 14, shield: 0, shieldRegenPerSec: 0,
  leak: 4, bounty: 1.45, radius: 9, flocks: false,
  resist: { kinetic: 0.35, energy: 0.75, explosive: 0.8, corrosive: 1.25 },
  immune: ['knockback', 'stun', 'slow'],
}, { laneSpread: 0.15, speedJitter: 0.02, bankStiffness: 26, bankDamping: 10.0 });

/**
 * CITADEL, act VI. Armour 24 against a pierce cap near 3, so a 20-damage shot
 * lands 36 and loses 24 of it. Rapid fire is not slow against this, it is
 * *zero*, and that distinction is the whole family restated at a magnitude the
 * last quarter of the game can feel.
 */
define({
  id: 'armoured.citadel', family: 'armoured', glyph: 'armouredCitadel', colour: ROLE.armoured,
  hp: 1800, speed: 12, armour: 24, shield: 0, shieldRegenPerSec: 0,
  leak: 7, bounty: 1.55, radius: 11, flocks: false,
  resist: { kinetic: 0.30, energy: 0.70, explosive: 0.75, corrosive: 1.30 },
  immune: ['knockback', 'stun', 'slow'],
}, { laneSpread: 0.12, speedJitter: 0.01, bankStiffness: 22, bankDamping: 10.4 });

// ───────────────────────────────────────────────────────────── swarmling ──
// The flocking family. Individually trivial, collectively a wall. These are
// the enemies the whole Flock solver exists for: they cluster, jostle, bank
// through corners and spill wide on the outside of a turn.

define({
  id: 'swarmling', family: 'swarmling', glyph: 'swarmling', colour: ROLE.swarmling,
  hp: 9, speed: 66, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 0.7, radius: 3, flocks: true,
  resist: { explosive: 1.35 },
}, { laneSpread: 0.8, speedJitter: 0.16, bankStiffness: 110, bankDamping: 5.8 });

define({
  id: 'swarmling.mite', family: 'swarmling', glyph: 'swarmlingMite', colour: ROLE.swarmling,
  hp: 4, speed: 84, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 0.5, radius: 2, flocks: true,
  resist: { explosive: 1.5 },
}, { laneSpread: 0.9, speedJitter: 0.2, bankStiffness: 130, bankDamping: 5.4 });

define({
  id: 'swarmling.hornet', family: 'swarmling', glyph: 'swarmlingHornet', colour: ROLE.swarmling,
  hp: 18, speed: 94, armour: 1, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 0.9, radius: 3, flocks: true,
  resist: { explosive: 1.2, kinetic: 0.8 },
  immune: ['knockback'],
}, { laneSpread: 0.85, speedJitter: 0.18, bankStiffness: 124, bankDamping: 5.5 });

define({
  id: 'swarmling.drone', family: 'swarmling', glyph: 'swarmlingDrone', colour: ROLE.swarmling,
  hp: 34, speed: 58, armour: 3, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 1.0, radius: 4, flocks: true,
  resist: { explosive: 1.15, kinetic: 0.7 },
}, { laneSpread: 0.7, speedJitter: 0.12, bankStiffness: 92, bankDamping: 6.2 });

// ──────────────────────────────────────────────────────────────── weaver ──
// Sweeps the full corridor width on a sine, phase-varied per instance so a
// group fans out instead of moving as one ribbon. A tower with a 20-degree
// cone parked beside a straight will watch weavers slide out of it twice a
// second. Wide arcs and splash answer them; single-target snipers do not.

define({
  id: 'weaver', family: 'weaver', glyph: 'weaver', colour: ROLE.weaver,
  hp: 58, speed: 46, armour: 1, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 1.15, radius: 5, flocks: false,
  resist: { kinetic: 0.75 },
}, { weaveAmp: 0.86, weaveHz: 0.55, laneSpread: 0.18, speedJitter: 0.07, bankStiffness: 74, bankDamping: 6.4 });

define({
  id: 'weaver.phantom', family: 'weaver', glyph: 'weaverPhantom', colour: ROLE.weaver,
  hp: 125, speed: 58, armour: 2, shield: 0, shieldRegenPerSec: 0,
  leak: 2, bounty: 1.25, radius: 5, flocks: false,
  resist: { kinetic: 0.5, energy: 1.3, explosive: 0.85 },
  immune: ['knockback'],
}, { weaveAmp: 0.95, weaveHz: 0.78, laneSpread: 0.2, speedJitter: 0.09, bankStiffness: 84, bankDamping: 6.0 });

define({
  id: 'weaver.mirage', family: 'weaver', glyph: 'weaverMirage', colour: ROLE.weaver,
  hp: 300, speed: 52, armour: 4, shield: 0, shieldRegenPerSec: 0,
  leak: 2, bounty: 1.35, radius: 6, flocks: false,
  resist: { kinetic: 0.55, energy: 1.15, corrosive: 0.4 },
  immune: ['dot', 'knockback'],
}, { weaveAmp: 1.0, weaveHz: 0.42, laneSpread: 0.24, speedJitter: 0.05, bankStiffness: 70, bankDamping: 6.6 });

/**
 * ECLIPSE, act VII. The weaver problem and the shielded problem in one body:
 * it sweeps the whole corridor at 0.9Hz *and* carries 260 points of regenerating
 * overshield, so a tower that can track it often cannot break it and a tower
 * that can break it cannot keep it in the cone. The last family the game
 * introduces, and the only one that asks two questions at once.
 */
define({
  id: 'weaver.eclipse', family: 'weaver', glyph: 'weaverEclipse', colour: ROLE.weaver,
  hp: 900, speed: 62, armour: 8, shield: 260, shieldRegenPerSec: 48,
  leak: 5, bounty: 1.5, radius: 7, flocks: false,
  resist: { kinetic: 0.45, energy: 1.1, corrosive: 0.5, explosive: 0.8 },
  immune: ['dot', 'knockback', 'stun'],
}, { weaveAmp: 1.0, weaveHz: 0.9, laneSpread: 0.26, speedJitter: 0.06, bankStiffness: 96, bankDamping: 5.8 });

// ────────────────────────────────────────────────────────────── splitter ──
// Four generations: prime → major → minor → mote. One prime that reaches the
// back of the kill zone becomes 2 majors, 6 minors and 24 motes — 33 bodies.
// The lesson is that the last third of the track needs coverage too, and that
// overkill damage on the prime is wasted because the children spawn at full
// health regardless.

define({
  id: 'splitter.prime', family: 'splitter', glyph: 'splitterPrime', colour: ROLE.splitter,
  hp: 190, speed: 30, armour: 3, shield: 0, shieldRegenPerSec: 0,
  leak: 3, bounty: 1.0, radius: 8, flocks: false,
  resist: { explosive: 0.7, corrosive: 1.2 },
  spawnsOnDeath: [{ id: 'splitter.major', count: 2 }],
}, { laneSpread: 0.3, speedJitter: 0.04, bankStiffness: 44, bankDamping: 8.2 });

define({
  id: 'splitter.major', family: 'splitter', glyph: 'splitterMajor', colour: ROLE.splitter,
  hp: 84, speed: 38, armour: 2, shield: 0, shieldRegenPerSec: 0,
  leak: 2, bounty: 0.95, radius: 6, flocks: false,
  resist: { explosive: 0.8, corrosive: 1.2 },
  spawnsOnDeath: [{ id: 'splitter.minor', count: 3 }],
}, { laneSpread: 0.45, speedJitter: 0.07, bankStiffness: 60, bankDamping: 7.4 });

define({
  id: 'splitter.minor', family: 'splitter', glyph: 'splitterMinor', colour: ROLE.splitter,
  hp: 32, speed: 48, armour: 1, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 0.85, radius: 4, flocks: true,
  resist: { explosive: 1.0, corrosive: 1.2 },
  spawnsOnDeath: [{ id: 'splitter.mote', count: 4 }],
}, { laneSpread: 0.6, speedJitter: 0.12, bankStiffness: 86, bankDamping: 6.4 });

define({
  id: 'splitter.mote', family: 'splitter', glyph: 'splitterMote', colour: ROLE.splitter,
  hp: 11, speed: 60, armour: 0, shield: 0, shieldRegenPerSec: 0,
  leak: 1, bounty: 0.6, radius: 3, flocks: true,
  resist: { explosive: 1.4 },
}, { laneSpread: 0.85, speedJitter: 0.18, bankStiffness: 118, bankDamping: 5.6 });

/**
 * LEVIATHAN, act V. Two primes on death, so one body is sixty-seven, and the
 * chain is four generations deeper than anything before it. The lesson is the
 * splitter lesson at act V's scale: a kill zone that ends two thirds along the
 * track has nothing behind it, and a leviathan popped late seeds the last third
 * with a round's worth of bodies.
 */
define({
  id: 'splitter.leviathan', family: 'splitter', glyph: 'splitterLeviathan', colour: ROLE.splitter,
  hp: 1400, speed: 24, armour: 8, shield: 0, shieldRegenPerSec: 0,
  leak: 6, bounty: 1.5, radius: 11, flocks: false,
  resist: { explosive: 0.65, corrosive: 1.15, kinetic: 0.7 },
  spawnsOnDeath: [{ id: 'splitter.prime', count: 2 }],
  immune: ['knockback', 'stun'],
}, { laneSpread: 0.2, speedJitter: 0.03, bankStiffness: 32, bankDamping: 9.4 });

// ────────────────────────────────────────────────────────────── shielded ──
// The overshield eats damage first and regenerates after a short lull. A
// 400-damage alpha strike on a 60-point shield wastes 340. A 30 DPS beam
// strips it in two seconds and keeps it stripped. Regen delay is the tuning
// knob: long enough that sustained fire wins, short enough that a slow
// single-shot tower never gets through.

define({
  id: 'shielded', family: 'shielded', glyph: 'shielded', colour: ROLE.shielded,
  hp: 62, speed: 30, armour: 1, shield: 60, shieldRegenPerSec: 7,
  leak: 2, bounty: 1.2, radius: 6, flocks: false,
  resist: { energy: 0.8 },
}, { laneSpread: 0.3, speedJitter: 0.05, bankStiffness: 52, bankDamping: 7.8 });

define({
  id: 'shielded.warden', family: 'shielded', glyph: 'shieldedWarden', colour: ROLE.shielded,
  hp: 150, speed: 26, armour: 3, shield: 180, shieldRegenPerSec: 34,
  leak: 3, bounty: 1.3, radius: 7, flocks: false,
  resist: { energy: 0.6, kinetic: 0.9 },
  immune: ['stun'],
}, { laneSpread: 0.26, speedJitter: 0.04, bankStiffness: 46, bankDamping: 8.2 });

define({
  id: 'shielded.aegis', family: 'shielded', glyph: 'shieldedAegis', colour: ROLE.shielded,
  hp: 340, speed: 24, armour: 6, shield: 440, shieldRegenPerSec: 60,
  leak: 5, bounty: 1.45, radius: 8, flocks: false,
  resist: { energy: 0.5, kinetic: 0.8, explosive: 0.85 },
  immune: ['stun', 'dot', 'knockback'],
}, { laneSpread: 0.22, speedJitter: 0.03, bankStiffness: 42, bankDamping: 8.6 });

// ────────────────────────────────────────────────────────────────── boss ──
// One per boss round. Each takes a family's threat and pushes it past the
// point where a single tower can answer it, and each leaks enough lives that
// letting one through ends a hundred-life run early.

/**
 * COLOSSUS, round 20 — armour wall. Nothing rapid-fire scratches it.
 *
 * The plate is 6, not 13, and kinetic bites for 0.6 rather than 0.45. Flat
 * armour lands per hit and act II's shelf is three towers deep: a Turret on
 * round 20 lands about twenty points, 0.45 of that is nine, and nine against
 * thirteen of plate is *nothing at all*. Measured, the whole act II board could
 * not remove a point of it and the round was a guaranteed twenty-five lives.
 * A boss should ask for the right answer, not refuse every answer.
 */
define({
  id: 'boss.colossus', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 9000, speed: 14, armour: 6, shield: 0, shieldRegenPerSec: 0,
  leak: 25, bounty: 1.6, radius: 14, flocks: false,
  resist: { kinetic: 0.6, explosive: 0.7, energy: 0.85, corrosive: 1.2 },
  immune: ['knockback', 'stun', 'slow'],
}, { laneSpread: 0.08, speedJitter: 0, bankStiffness: 18, bankDamping: 11.0 });

/** HIVEMIND, round 40 — dies into forty hornets. Kill it early or not at all. */
define({
  id: 'boss.hivemind', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 7200, speed: 27, armour: 7, shield: 0, shieldRegenPerSec: 0,
  leak: 30, bounty: 1.5, radius: 12, flocks: false,
  resist: { explosive: 0.6, energy: 0.9 },
  spawnsOnDeath: [
    { id: 'swarmling.hornet', count: 28 },
    { id: 'swarmling.drone', count: 12 },
  ],
  immune: ['knockback', 'stun'],
}, { laneSpread: 0.12, speedJitter: 0, bankStiffness: 40, bankDamping: 8.4 });

/** PHASE, round 60 — 5000 shield at 420/sec. Burst alone never lands a point. */
define({
  id: 'boss.phase', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 6000, speed: 31, armour: 6, shield: 5200, shieldRegenPerSec: 420,
  leak: 35, bounty: 1.55, radius: 12, flocks: false,
  resist: { energy: 0.55, kinetic: 0.75 },
  immune: ['slow', 'stun', 'knockback', 'dot'],
}, { weaveAmp: 0.5, weaveHz: 0.3, laneSpread: 0.12, speedJitter: 0, bankStiffness: 56, bankDamping: 7.2 });

/** QUICKSILVER, round 80 — 78px/s and immune to every form of control. */
define({
  id: 'boss.quicksilver', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 13000, speed: 78, armour: 10, shield: 900, shieldRegenPerSec: 120,
  leak: 45, bounty: 1.7, radius: 11, flocks: false,
  resist: { explosive: 0.5, kinetic: 0.8, corrosive: 0.7 },
  immune: ['slow', 'stun', 'knockback', 'dot'],
}, { weaveAmp: 0.4, weaveHz: 0.9, laneSpread: 0.2, speedJitter: 0, bankStiffness: 108, bankDamping: 5.6 });

/** SINGULARITY, round 100 — armour, shield and a four-way split on death. */
define({
  id: 'boss.singularity', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 42000, speed: 22, armour: 32, shield: 13000, shieldRegenPerSec: 640,
  leak: 100, bounty: 2.0, radius: 16, flocks: false,
  resist: { kinetic: 0.4, energy: 0.55, explosive: 0.6, corrosive: 0.7, arcane: 0.8 },
  spawnsOnDeath: [{ id: 'boss.shard', count: 4 }],
  immune: ['slow', 'stun', 'knockback', 'dot'],
}, { laneSpread: 0.06, speedJitter: 0, bankStiffness: 22, bankDamping: 10.4 });

/** Shards of the singularity. Faster, unshielded, still armoured. */
define({
  id: 'boss.shard', family: 'boss', glyph: 'boss', colour: ROLE.boss,
  hp: 6400, speed: 36, armour: 15, shield: 0, shieldRegenPerSec: 0,
  leak: 15, bounty: 1.4, radius: 9, flocks: true,
  resist: { kinetic: 0.6, energy: 0.75 },
  immune: ['stun', 'knockback'],
}, { laneSpread: 0.5, speedJitter: 0.08, bankStiffness: 78, bankDamping: 6.6 });

// ──────────────────────────────────────────────────────────────── lookup ──

export const ENEMIES: Record<string, EnemyArchetype> = {};
for (const a of ENEMY_LIST) ENEMIES[a.id] = a;

export const ENEMY_IDS: readonly string[] = ENEMY_LIST.map((a) => a.id);

/** Boss ids in the order `WAVES.bossRounds` expects them. */
export const BOSS_IDS: readonly string[] = [
  'boss.colossus',
  'boss.hivemind',
  'boss.phase',
  'boss.quicksilver',
  'boss.singularity',
];

export function enemyById(id: string): EnemyArchetype {
  const a = ENEMIES[id];
  if (!a) throw new Error(`unknown enemy archetype "${id}"`);
  return a;
}

export function motionOf(id: string): MotionProfile {
  return MOTION[id] ?? DEFAULT_MOTION;
}

export function familyOf(family: EnemyFamily): EnemyArchetype[] {
  return ENEMY_LIST.filter((a) => a.family === family);
}

/**
 * Total bodies produced by one spawn, following the split chain to the bottom.
 * Waves.ts uses this so a round of ten primes is priced as 330 enemies rather
 * than ten, and the pool is sized for the worst case.
 */
export function totalBodies(id: string, depth = 0): number {
  if (depth > 8) return 1;
  const a = enemyById(id);
  let n = 1;
  if (a.spawnsOnDeath) {
    for (const s of a.spawnsOnDeath) n += s.count * totalBodies(s.id, depth + 1);
  }
  return n;
}

/** Effective hit points including shield and the whole split chain. */
export function totalRbe(id: string, depth = 0): number {
  if (depth > 8) return 0;
  const a = enemyById(id);
  let n = a.hp + a.shield;
  if (a.spawnsOnDeath) {
    for (const s of a.spawnsOnDeath) n += s.count * totalRbe(s.id, depth + 1);
  }
  return n;
}

/**
 * Fails loudly on a dangling or cyclic split chain. Called once at boot; a
 * cycle here would be an infinite spawn loop at runtime rather than a crash,
 * which is far harder to notice.
 */
export function validateCatalog(): void {
  const seen = new Set<string>();
  const walk = (id: string, stack: string[]): void => {
    if (stack.includes(id)) throw new Error(`split cycle: ${stack.join(' → ')} → ${id}`);
    const a = enemyById(id);
    if (!a.spawnsOnDeath) return;
    stack.push(id);
    for (const s of a.spawnsOnDeath) {
      if (s.count <= 0) throw new Error(`${id} spawns ${s.count} of ${s.id}`);
      walk(s.id, stack);
    }
    stack.pop();
  };
  for (const a of ENEMY_LIST) {
    if (seen.has(a.id)) throw new Error(`duplicate archetype id "${a.id}"`);
    seen.add(a.id);
    if (a.radius <= 0) throw new Error(`${a.id} has radius ${a.radius}`);
    if (a.speed <= 0) throw new Error(`${a.id} has speed ${a.speed}`);
    if (a.shield > 0 && a.shieldRegenPerSec <= 0) {
      throw new Error(`${a.id} has a shield that never regenerates`);
    }
  }
  for (const a of ENEMY_LIST) walk(a.id, []);
  for (const id of BOSS_IDS) enemyById(id);
}
